// Wave 20.26 — FIT file import via drag-and-drop.
//
// Web-only for now — mobile users record natively so they don't need
// this. Riders on Garmin / Wahoo / Zwift can export a .fit and drop it
// here; the server parses + creates a session_summary + fires peak
// detection the same way a native recording does.

import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import PageLayout from './components/PageLayout';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

export default function ImportPage() {
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = (f: File | null) => {
    setError(null);
    if (!f) {
      setFile(null);
      return;
    }
    // Loose extension check — server does the real validation. Just
    // catch obvious misdrops early so the user doesn't wait for a
    // 400 from parsing a JPEG.
    const name = f.name.toLowerCase();
    if (!name.endsWith('.fit')) {
      setError('Please drop a .fit file. Garmin / Wahoo / Zwift all export FIT.');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setError('File is over 20 MB — mail hello@aero.chat if you need this raised.');
      return;
    }
    setFile(f);
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0] ?? null;
    pick(f);
  }, []);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const jwt = localStorage.getItem(TOKEN_KEY);
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`${API}/activities/import`, {
        method: 'POST',
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Import failed (${res.status})`);
      // Success — jump to the fresh session detail page with ?saved=1
      // so the title/description editor auto-opens pre-filled with
      // the server recommendation.
      nav(`/sessions/${data.id}?saved=1`);
    } catch (e: any) {
      setError(e?.message || 'Import failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <PageLayout maxWidth="sm">
      <Stack spacing={3} sx={{ py: 4 }}>
        <Box>
          <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
            Import
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Upload a FIT file
          </Typography>
          <Typography sx={{ color: colors.textMuted, mt: 1 }}>
            Drag the .fit export from your Garmin, Wahoo, or Zwift onto the box below.
            We'll parse it, run peak detection, and drop it into your feed.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: `2px dashed ${dragOver ? colors.brand : colors.border}`,
            backgroundColor: dragOver ? `${colors.brand}0f` : colors.surface,
            borderRadius: 3,
            p: 6,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'background-color 120ms, border-color 120ms',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 56, color: colors.brand, mb: 1 }} />
          <Typography sx={{ color: colors.text, fontWeight: 700, fontSize: '1.05rem' }}>
            {file ? file.name : 'Drop a .fit file here'}
          </Typography>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.5 }}>
            {file
              ? `${(file.size / 1024).toFixed(1)} KB — click Upload below`
              : 'or click to choose a file (max 20 MB)'}
          </Typography>
          <input
            ref={inputRef}
            type="file"
            accept=".fit,application/octet-stream"
            style={{ display: 'none' }}
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
          />
        </Box>

        {uploading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: colors.brand } }} />}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={upload}
            disabled={!file || uploading}
            sx={{
              backgroundColor: colors.brand,
              color: colors.textInverse,
              fontWeight: 800,
              px: 4,
            }}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
          {file && !uploading && (
            <Button variant="text" onClick={() => setFile(null)} sx={{ color: colors.textMuted }}>
              Clear
            </Button>
          )}
        </Stack>

        <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', mt: 3 }}>
          FIT files from indoor smart trainers work too — but if there's no GPS in the
          file, the resulting session won't have a map.
        </Typography>
      </Stack>
    </PageLayout>
  );
}
